import { createContext, useState, useMemo, useContext } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

type ToastContextProviderType = {
  children: JSX.Element;
};

export type ToastContextType = {
  setMessage: (message: string) => void;
  showSuccessFeedback: (feedback: string) => void;
  showErrorFeedback: (feedback: string) => void;
  setSeverity: (severity: AlertColor) => void;
  closeToast: () => void;
  showToast: () => void;
};

const defaultState = {
  showSuccessFeedback: () => {},
  showErrorFeedback: () => {},
  setMessage: () => {},
  closeToast: () => {},
  showToast: () => {},
  setSeverity: () => {},
};

const ToastContext = createContext<ToastContextType>(defaultState);

const Toast = function ({ children }: ToastContextProviderType): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [message, setMessage] = useState<string>('');
  const [severity, setSeverity] = useState<AlertColor>('success');

  const closeToast = (): void => {
    setIsOpen(false);
  };

  const showToast = (): void => {
    setIsOpen(true);
  };

  const showSuccessFeedback = (feedback: string): void => {
    setMessage(feedback);
    setSeverity('success');
    showToast();
  };

  const showErrorFeedback = (feedback: string): void => {
    setMessage(feedback);
    setSeverity('error');
    showToast();
  };

  const values = useMemo(() => {
    return {
      setMessage,
      closeToast,
      showToast,
      setSeverity,
      showSuccessFeedback,
      showErrorFeedback,
    };
  }, []);

  return (
    <ToastContext.Provider value={values}>
      {children}
      <Snackbar open={isOpen && !!message} autoHideDuration={6000} onClose={closeToast}>
        <Alert onClose={closeToast} severity={severity} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export default Toast;

export const useToastContext = (): ToastContextType => {
  return useContext(ToastContext);
};
